import React, { useEffect, useState } from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import Radio from "@mui/material/Radio";
import AutopilotSettings from "./AutopilotSettings.tsx";
import apiService from "../services/api";

const MenuItems = ({ lead, handleChangeLeadData, handleCloseResponseMenu }) => {
  const handleAnswerTypeChange = async (answerType: 'Manual' | 'Suggested' | 'Autopilot') => {
    try {
      // Update local state immediately for UI responsiveness
      handleChangeLeadData(lead.id, "answerType", answerType);
      
      // Send to backend
      const result = await apiService.configureAutopilot(lead.id, { answerType });
      if (!result.success) {
        console.error('Failed to configure autopilot:', result.error);
        // Revert local state on error
        handleChangeLeadData(lead.id, "answerType", lead.answerType || "Manual");
      }
    } catch (error) {
      console.error('Error configuring autopilot:', error);
      // Revert local state on error
      handleChangeLeadData(lead.id, "answerType", lead.answerType || "Manual");
    }
  };

  return (
    <>
      <MenuItem
        onClick={() => {
          handleAnswerTypeChange("Manual");
          handleCloseResponseMenu();
        }}
        sx={{
          fontFamily: "Inter , sans-serif",
          fontSize: "16px",
          fontWeight: "600",
          color: "#222830",
          padding: "15px",
          display: "flex",
          alignItems: "center",
          gap: "24px",
        }}
      >
        <Radio
          checked={lead?.answerType === "Manual"}
          size="small"
          sx={{
            padding: 0,
            "&.Mui-checked": {
              color: "#222830",
            },
          }}
        />
        Manual Response
      </MenuItem>
      <Divider sx={{ margin: "0 !important", borderColor: "#323C48" }} />
      <MenuItem
        onClick={() => {
          handleAnswerTypeChange("Suggested");
          handleCloseResponseMenu();
        }}
        sx={{
          fontFamily: "Inter , sans-serif",
          fontSize: "16px",
          fontWeight: "600",
          color: "#222830",
          padding: "15px",
          display: "flex",
          alignItems: "center",
          gap: "24px",
        }}
      >
        <Radio
          checked={lead?.answerType === "Suggested"}
          size="small"
          sx={{
            padding: 0,
            "&.Mui-checked": {
              color: "#222830",
            },
          }}
        />
        Suggested Response
      </MenuItem>
      <Divider sx={{ margin: "0 !important", borderColor: "#323C48" }} />
      <MenuItem
        onClick={() => {
          handleAnswerTypeChange("Autopilot");
          handleCloseResponseMenu();
        }}
        sx={{
          fontFamily: "Inter , sans-serif",
          fontSize: "16px",
          fontWeight: "600",
          color: "#222830",
          padding: "15px",
          display: "flex",
          alignItems: "center",
          gap: "24px",
        }}
      >
        <Radio
          checked={lead?.answerType === "Autopilot"}
          size="small"
          sx={{
            padding: 0,
            "&.Mui-checked": {
              color: "#222830",
            },
          }}
        />
        Autopilot Response
      </MenuItem>
      <AutopilotSettings
        lead={lead}
        handleChangeLeadData={handleChangeLeadData}
        handleCloseResponseMenu={handleCloseResponseMenu}
      />
    </>
  );
};

const ResponseMenu = ({ lead, handleChangeLeadData }) => {
  const [anchorResponse, setAnchorResponse] = useState(null);

  const openResponse = Boolean(anchorResponse);

  const handleCloseResponseMenu = () => {
    setAnchorResponse(null);
  };

  const handleClickResponseMenu = (event) => {
    setAnchorResponse(event.currentTarget);
  };

  // Ensure Autopilot settings persist across refresh by loading config
  useEffect(() => {
    const fetchAutopilot = async () => {
      try {
        if (!lead?.id) return;
        const cfg = await apiService.getAutopilotConfig(lead.id);
        if (!cfg) return;
        const nextAnswerType = cfg.answerType || lead.answerType || "Manual";
        if (nextAnswerType !== lead.answerType) {
          handleChangeLeadData(lead.id, "answerType", nextAnswerType);
        }
        if (nextAnswerType === "Autopilot") {
          if ((cfg.instantly ?? true) !== (lead.instally ?? lead.instally)) {
            handleChangeLeadData(lead.id, "instantly", cfg.instantly ?? true);
          }
          if (cfg.parsedTime && cfg.parsedTime !== (lead.parsedTime as any)) {
            handleChangeLeadData(lead.id, "parsedTime", new Date(cfg.parsedTime as any));
          }
          if (cfg.autopilotTime !== lead.autopilotTime) {
            handleChangeLeadData(lead.id, "autopilotTime", cfg.autopilotTime);
          }
        }
      } catch (e) {
        console.warn("Failed to load autopilot config", e);
      }
    };
    fetchAutopilot();
    // run on mount and whenever we switch leads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id]);

  return (
    <>
      <Button
        id="response-button"
        aria-controls={openResponse ? "response-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={openResponse ? "true" : undefined}
        onClick={handleClickResponseMenu}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          border: "1px solid",
          borderColor: "lightGray",
          fontFamily: "Inter , sans-serif",
          textTransform: "none",
          paddingLeft: "20px",
          paddingRight: "20px",
          borderRadius: "24px",
          height: "40px",
          "&:hover": {
            backgroundColor: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <p className="text-h3 font-medium text-deepBlack">
          {`${lead?.answerType} Response`}
          {lead?.answerType === "Autopilot" &&
            !!lead?.autopilotTime &&
            !lead?.instantly && (
              <span className="font-bold text-darkBlue">
                {`${` (${lead?.autopilotTime})`}`}
              </span>
            )}
        </p>
        <i
          className={`fa-solid fa-chevron-${openResponse ? "up" : "down"
            } text-deepBlack`}
        ></i>
      </Button>
      <Menu
        id="response-menu"
        anchorEl={anchorResponse}
        open={openResponse}
        onClose={handleCloseResponseMenu}
        MenuListProps={{
          "aria-labelledby": "basic-button",
          sx: {
            paddingTop: 0,
            paddingBottom: 0,
          },
        }}
        elevation={0}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          style: {
            width: "320px",
            borderRadius: "8px",
            border: "1px solid #222830",
            background: "#F3F5F7",
            boxShadow: "0 4px 24px #CBD5E066",
            transform: "translateY(10px)",
          },
        }}
      >
        <MenuItems
          lead={lead}
          handleChangeLeadData={handleChangeLeadData}
          handleCloseResponseMenu={handleCloseResponseMenu}
        />
      </Menu>
    </>
  );
};

export default ResponseMenu;
export { MenuItems };
